import axios from "axios";
import moment from "moment";
import { i18n } from "@/main.js";

var url = process.env.VUE_APP_BACKEND_URL || "http://localhost:8000/api/";

const getApiClient = (useBaseUrl = true) => {
  return axios.create({
    baseURL: useBaseUrl ? url : null,
    withCredentials: false,
    withXSRFToken: true,
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      Accept: "application/json",
    },
  });
};

const apiClientWithAuth = (useBaseUrl = true) => {
  const token = localStorage.getItem("token") || false;
  if (!token) {
    throw new Error("Token not found");
  }
  const apiClient = getApiClient(useBaseUrl);
  apiClient.defaults.headers.common["Authorization"] = "Bearer " + token;
  return apiClient;
};

const apiClient = getApiClient();

const setToken = (token) => {
  localStorage.setItem("token", token);
};

const removeToken = () => {
  localStorage.removeItem("token");
};

export const getUser = async () => {
  try {
    const response = await apiClientWithAuth().get("me");
    if (response.status >= 200 && response.status < 300) {
      const data = response.data;
      data.birth_date = moment(data.birth_date).format("YYYY-MM-DD");
      return data;
    }
    removeToken();
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const loginUser = async (data) => {
  try {
    removeToken();
    const response = await apiClient.post("login", data);
    if (response.status >= 200 && response.status < 300) {
      setToken(response.data.token);
      return {
        errors: [],
      };
    }
    if (response.status >= 400 && response.status < 500) {
      let error = response.data.message;
      if (response.status === 422) {
        error = i18n.global.t('api_errors.incorrect_credentials');
      }
      return {
        errors: [error],
      };
    }
    return {
      errors: [i18n.global.t('api_errors.server_error')],
    };
  } catch (e) {
    console.log(e);
    let error = e.response.data.message;
    if (e.response.status === 422) {
      error = i18n.global.t('api_errors.incorrect_credentials');
    }
    return {
      errors: [error],
    };
  }
};

export const logoutUser = async () => {
  try {
    const response = await apiClientWithAuth().post("me/logout");
    if (response.status >= 200 && response.status < 300) {
      removeToken();
      return true;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getReservations = async (url = false) => {
  try {
    const finalUrl = url ? url : "me/reservations";
    const response = await apiClientWithAuth(!url).get(finalUrl);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getTransactions = async (url = false) => {
  try {
    const finalUrl = url ? url : "me/transactions";
    const response = await apiClientWithAuth(!url).get(finalUrl);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getPackages = async () => {
  try {
    const response = await apiClientWithAuth().get("me/packages");
    if (response.status >= 200 && response.status < 300) {
      return response.data.packages;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getTerms = async (url = false) => {
  try {
    const finalUrl = url ? url : "me/terms";
    const response = await apiClientWithAuth(!url).get(finalUrl);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const registerUser = async (data) => {
  try {
    const response = await apiClient.post("/register", data);
    if (response.status >= 200 && response.status < 300) {
      console.log(response.data);
      return {
        success: true,
        errors: response.data?.errors,
      };
    } else {
      return {
        success: false,
        errors: false,
      };
    }
  } catch (e) {
    console.log(e);
    if (e?.response?.status === 422) {
      return {
        success: false,
        errors: e.response.data,
      };
    } else {
      return {
        success: false,
        errors: false,
      };
    }
  }
};

export const editUser = async (data) => {
  try {
    const response = await apiClientWithAuth().patch("me", data);
    if (response.status >= 200 && response.status < 300) {
      return true;
    }
    if (response.data?.errors) {
      return response.data.errors;
    }
    return false;
  } catch (e) {
    console.log(e);
    if (e.response.data?.errors) {
      return e.response.data;
    }
    return false;
  }
};

export const allBranches = async () => {
    try {
        const {data} = await  apiClient.get('/freeterms/branches');
        if (data?.branches) {
            return data.branches;
        }
        return [];
    } catch (e) {
        console.warn(e)
        return [];
    }
}

export const freeTerms = async (month, year, branchId) => {
  try {
    const response = await apiClient.get(`freeterms/${branchId}/${month}/${year}`);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const makeReservation = async (data, isLogged) => {
  try {
    let api = apiClient;
    if (isLogged) {
      api = apiClientWithAuth();
    }
    const response = await api.post("reservations", data);
    if (response.status >= 200 && response.status < 300) {
      return { ok: true, message: response.data.message, data: response.data };
    }
    return {
      ok: false,
      message: i18n.global.t('api_errors.reservation_create_failed'),
    };
  } catch (error) {
    if ([400, 401, 403, 409].includes(error.response.status)) {
      return { ok: false, message: error.response.data.error };
    }
    return {
      ok: false,
      message: i18n.global.t('api_errors.reservation_create_failed'),
    };
  }
};

export const getReservation = async (reservationId, isLogged) => {
  try {
    let api = apiClient;
    if (isLogged) {
      api = apiClientWithAuth();
    }
    const response = await api.get("reservations/" + reservationId);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const requestPasswordReset = async (data) => {
  try {
    const response = await apiClient.post("forgot-password", data);
    if (response.status > 200 && response.status < 300) {
      return true;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await apiClient.post("forgot-password", { email });
    if (response.status >= 200 && response.status < 300) {
      return true;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const resetPassword = async (state, token, email) => {
  try {
    const { password, password_confirmation } = state;
    const response = await apiClient.post("reset-password", {
      password,
      password_confirmation,
      token,
      email,
    });
    if (response.status >= 200 && response.status < 300) {
      return true;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const verifyEmail = async (id, hash) => {
  try {
    const response = await apiClient.get(`verify-email/${id}/${hash}`);
    if (response.status > 200 && response.status < 300) {
      return true;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const cancelReservation = async (id) => {
  try {
    const response = await apiClientWithAuth().delete(`me/reservations/${id}`);
    if (response.status >= 200 && response.status < 300) {
      return { ok: true, message: response.data.message, data: response.data };
    }
    return {
      ok: false,
      message: i18n.global.t('api_errors.reservation_cancel_failed'),
    };
  } catch (error) {
    if (
      error.response &&
      [400, 401, 403, 409].includes(error.response.status)
    ) {
      return { ok: false, message: error.response.data.error };
    } else {
      return {
        ok: false,
        message: i18n.global.t('api_errors.term_cancel_failed'),
      };
    }
  }
};

export const cancelTerm = async (id) => {
  try {
    const response = await apiClientWithAuth().delete(`me/terms/${id}`);
    if (response.status >= 200 && response.status < 300) {
      return { ok: true, message: response.data.message, data: response.data };
    }
    return {
      ok: false,
      message: i18n.global.t('api_errors.term_cancel_failed'),
    };
  } catch (error) {
    if (
      error.response &&
      [400, 401, 403, 409].includes(error.response.status)
    ) {
      return { ok: false, message: error.response.data.error };
    } else {
      return {
        ok: false,
        message: i18n.global.t('api_errors.term_cancel_failed'),
      };
    }
  }
};

export const payReservation = async (id) => {
  try {
    const response = await apiClientWithAuth().post(`reservations/${id}/pay`);
    if (response.status > 200 && response.status < 300) {
      return true;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const createTransaction = async (data) => {
  try {
    const response = await apiClientWithAuth().post("me/transactions", data);
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.transaction?.payment_link;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getTransaction = async (id) => {
  try {
    const response = await apiClientWithAuth().get("me/transactions/" + id);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const closestTerms = async (branchId) => {
  try {
    const response = await apiClient.get(`freeterms/${branchId}/nearest`);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const recheckTerms = async (termIds) => {
  try {
    const response = await apiClient.post('freeterms/recheck', { term_ids: termIds });
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const checkCoupon = async (coupon, basket) => {
  try {
    const response = await apiClient.post(`coupons/validate`, {
      coupon,
      basket
    });
    if (response.status >= 200 && response.status < 300) {
      return { ok: true, message: response.message, data: response.data };
    } else {
      return {
        ok: false,
        message: i18n.global.t('api_errors.coupon_verify_failed'),
      };
    }
  } catch (error) {
    if ([400, 401, 403, 409].includes(error.response.status)) {
      return { ok: false, message: error.response.data.error };
    }
    return {
      ok: false,
      message: i18n.global.t('api_errors.coupon_verify_failed'),
    };
  }
};

export const updateDefaultMerchant = async ({ merchantId }) => {
  try {
    const payload = {};
    if (merchantId) {
      payload.merchant_id = merchantId;
    }
    const response = await apiClientWithAuth().patch("me/default-merchant", payload);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
};

